import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * Patched: stale responses are ignored, an unknown count is re-fetched instead of
 * being treated as 0, and the bubble animation can no longer get stuck.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** Incremented per cart event / render so older results can be discarded. */
  #updateId = 0;
  #renderId = 0;
  #reconcileId = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #reconcileTimer;

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
    // sessionStorage / bfcache / cached HTML can all be stale: always verify against the cart.
    this.scheduleReconcile();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.onPageShow);
    clearTimeout(this.#reconcileTimer);
  }

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
      this.scheduleReconcile();
    }
  };

  /**
   * Handles the cart update event.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onCartUpdate = (event) => {
    const updateId = ++this.#updateId;

    // Whatever happens to this event's promise, re-check the real cart shortly after.
    this.scheduleReconcile();

    event.promise
      ?.then(async ({ cart, detail }) => {
        // A newer cart event started after this one: its result is the truth.
        if (updateId !== this.#updateId) return;

        let itemCount = cart?.totalQuantity ?? detail?.itemCount;

        // Never guess 0 (that hides the badge). Ask the cart instead.
        if (typeof itemCount !== 'number' || Number.isNaN(itemCount)) {
          itemCount = await this.fetchCartCount();
          if (updateId !== this.#updateId || itemCount === null) return;
        }

        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      })
      .finally(() => this.scheduleReconcile());
  };

  /**
   * Debounced check of the badge against the server cart (the source of truth).
   * Covers aborted/failed/out-of-order events that would otherwise leave a stale count.
   */
  scheduleReconcile = () => {
    clearTimeout(this.#reconcileTimer);
    this.#reconcileTimer = setTimeout(async () => {
      const reconcileId = ++this.#reconcileId;
      const count = await this.fetchCartCount();

      // A newer reconcile (or a newer cart event) is in flight: let it decide.
      if (count === null || reconcileId !== this.#reconcileId) return;

      const visible = this.refs.cartBubbleCount.textContent ?? '';
      const expected = count < 100 ? String(count) : '';
      const hiddenWrong = this.refs.cartBubble.classList.contains('visually-hidden') !== (count === 0);

      if (visible !== expected || hiddenWrong) this.renderCartBubble(count, false);
    }, 400);
  };

  /**
   * Reads the live item count from the cart.
   * @returns {Promise<number | null>}
   */
  fetchCartCount = async () => {
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return null;
      const data = await response.json();
      return typeof data.item_count === 'number' ? data.item_count : null;
    } catch (_) {
      return null;
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    const renderId = ++this.#renderId;
    const { cartBubble, cartBubbleCount, cartBubbleText } = this.refs;

    // Always start from a clean animation state so a previous run can't leave it stuck.
    cartBubble.classList.remove('cart-bubble--animating');

    cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Let the browser see the state change before the animation starts.
    await new Promise((resolve) => requestAnimationFrame(resolve));
    if (renderId !== this.#renderId) return;

    cartBubble.classList.add('cart-bubble--animating');

    // animationend may never fire (cancelled, reduced motion, hidden), so cap the wait.
    await Promise.race([onAnimationEnd(cartBubbleText), new Promise((resolve) => setTimeout(resolve, 800))]);

    // Only the latest render may clear the class.
    if (renderId === this.#renderId) {
      cartBubble.classList.remove('cart-bubble--animating');
    }
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
